import React, { useState, useMemo, useEffect } from 'react';
import { formatDateRange, todayPacific } from '@/utils/displayDateTime';
import { Card, Row, Col, Button, Form } from '@/components/ui';
import { KPIHomeWidget } from '../../../components/widgets/KPIHomeWidget';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import ServiceAreaBarGraphWidget from '../../../components/widgets/ServiceAreaBarGraphWidget';
import { type PcrPieChartData } from '../../../components/widgets/PcrPieChartWidget';
import PcrStackedBarWidget from '../../../components/widgets/PcrStackedBarWidget';
import LabourStackedBarWidget from '../../../components/widgets/LabourStackedBarWidget';
import { useLabourDaily } from '../hooks/useLabourDaily';
import { COLOR_PALETTE } from '../../../components/widgets/shared/barGraphUtils';
import WorkIdTaskAssignmentWidget from '../../../components/widgets/WorkIdTaskAssignmentWidget';
import { SUBMISSIONS_BAR_CONFIG, NEW_WORK_IDS_BAR_CONFIG } from '../config/barGraphConfig';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useApprovalData } from '../../../hooks/useApprovalData';
import { useNewWorkIdCounts } from '../hooks/useNewWorkIdCounts';
import { useWorkIdTaskAssignmentSummary } from '../hooks/useWorkIdTaskAssignmentSummary';
import { queueHomeRequest } from '../homeRequestQueue';
import { useUrgentWorkIdCount } from '../hooks/useUrgentWorkIdCount';
import { useKPITaskCounts } from '../hooks/useKPITaskCounts';
import { usePendingSubmissionsCount } from '../hooks/usePendingSubmissionsCount';
import { useQuantifiedCompletedCounts } from '../hooks/useQuantifiedCompletedCounts';
import { useOverdueTaskCount } from '../hooks/useOverdueTaskCount';
import { usePlannedStartThisWeekCount } from '../hooks/usePlannedStartThisWeekCount';
import { usePcrHomeMetrics } from '../hooks/usePcrHomeMetrics';
import { KPI_WIDGETS } from '../config/kpiWidgetConfig';
import BarSegmentLegend from '../components/BarSegmentLegend';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useHomeAreaFilter, getForemanAreaOptions } from '../hooks/useHomeAreaFilter';
import { formService, type FormFilterOptions } from '../../../services/formService';
import CriticalDefectsWidget from '../../../components/widgets/CriticalDefectsWidget';
import { useCriticalDefects } from '../hooks/useCriticalDefects';
import PendingTransfersWidget, { PENDING_TRANSFERS_WIDGET_PERMISSION } from '../../../components/widgets/PendingTransfersWidget';
import { usePendingTransfers } from '../hooks/usePendingTransfers';
import EquipmentHomeWidget from '../../../components/widgets/EquipmentHomeWidget';
import { useEquipmentHome } from '../hooks/useEquipmentHome';
import { useVendorComplianceCounts } from '../hooks/useVendorComplianceCounts';
import { VENDOR_COMPLIANCE_SECTION } from '../../reporting/vendorCompliance';
import {
  getPcrWrapServiceAreaName,
  pcrChartMonthDateBounds,
  PCR_STATUS_OPTIONS,
} from '../../../services/pcrService';

// All widget permission keys - user needs at least one to see the widget section
const WIDGET_PERMISSION_KEYS = [
  'widget:pending_approvals_bar',
  'widget:pending_approvals_kpi',
  'widget:new_work_ids_bar',
  'widget:work_id_task_assignment',
  'widget:work_id_pending_action_kpi',
  'widget:urgent_work_ids_kpi',
  'widget:internal_review_kpi',
  'widget:active_tasks_kpi',
  'widget:overdue_tasks_kpi',
  'widget:planned_start_this_week_kpi',
  'widget:marked_complete_kpi',
  'widget:completed_last_week_kpi',
  'widget:completed_this_week_kpi',
  'widget:insurance_expiries',
  'widget:pending_transfers',
] as const;

// Map KPI widget id to permission key
const WIDGET_ID_TO_PERMISSION: Record<string, string> = {
  'kpi-internal-review': 'widget:internal_review_kpi',
  'kpi-active': 'widget:active_tasks_kpi',
  'kpi-marked-complete': 'widget:marked_complete_kpi',
};

const PLANNED_START_WIDGET_STATUSES = [
  'PROPOSED',
  'PLANNED',
  'ACTIVE',
  'ACTIVE_MARKED_AS_COMPLETE',
  'INTERNAL_REVIEW',
].join(',');

/** Overdue = still-open work only (matches the Overdue Tasks report): planned or active, not marked complete. */
const OVERDUE_TASK_WIDGET_STATUSES = ['PLANNED', 'ACTIVE'].join(',');

/** PCR dashboard filters only apply to these WRAP service area ids (same as server). */
const PCR_HOME_METRIC_SA_IDS = new Set([16, 17, 22, 25]);

function currentPcrChartMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatPcrChartMonthLabel(chartMonth: string): string {
  const [yearStr, monthStr] = chartMonth.split('-');
  const year = parseInt(yearStr ?? '', 10);
  const month = parseInt(monthStr ?? '', 10);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return chartMonth;
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** V3-023 / FMT-08: "2026-11-02 – 2026-11-08", from the shared formatter. */
function formatWeekRangeLabel(startStr: string, endStr: string): string {
  if (!startStr || !endStr) return '';
  return formatDateRange(startStr, endStr);
}

/** Set to true to show the Assigned Work IDs task-assignment widget on the home screen. */
const SHOW_WORK_ID_TASK_ASSIGNMENT_WIDGET = false;

function getCurrentWeekRangeLocal(): { start: string; end: string } {
  // The Pacific calendar day (FMT-24), as a local date for the weekday arithmetic.
  const [py, pm, pd] = todayPacific().split('-').map(Number);
  const today = new Date(py, pm - 1, pd);
  const day = today.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const start = new Date(today);
  start.setDate(today.getDate() + diffToMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return {
    start: start.toLocaleDateString('en-CA'),
    end: end.toLocaleDateString('en-CA'),
  };
}

const HomePage: React.FC = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const { hasPermission } = usePermissions();
  const canViewWidgets = WIDGET_PERMISSION_KEYS.some((key) => hasPermission(key));
  const statuses = KPI_WIDGETS.map((w) => w.status);

  const {
    selectedServiceAreaIds,
    selectedForemanAreaIds,
    setServiceAreaIds,
    setForemanAreaIds,
    setAreaFilters,
    serviceAreaOptions,
    foremanAreaOptions,
    serviceAreaNames,
    foremanAreaNames,
    foremanAreaIdForForms,
  } = useHomeAreaFilter(currentUser?.foremanAreas);

  // Tablet (iPad either way round): the filter row collapses behind a Filters toggle so the
  // dashboard starts near the top (W-iP9). Desktop always shows it.
  const isTabletWidth = useMediaQuery('(min-width: 768px) and (max-width: 1279.98px)');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [pendingServiceAreaId, setPendingServiceAreaId] = useState<number | null>(
    () => (selectedServiceAreaIds.length > 0 ? selectedServiceAreaIds[0] : null)
  );
  const [pendingForemanAreaId, setPendingForemanAreaId] = useState<number | null>(
    () => (selectedForemanAreaIds.length > 0 ? selectedForemanAreaIds[0] : null)
  );

  // Canonical service area names from forms API (same as Submissions page) so pending count filter matches DB
  const [formFilterOptions, setFormFilterOptions] = useState<FormFilterOptions | null>(null);
  const canFetchPendingApprovals = hasPermission('widget:pending_approvals_kpi');
  useEffect(() => {
    if (!canFetchPendingApprovals) return;
    queueHomeRequest(() => formService.getFilterOptions()).then(setFormFilterOptions).catch(() => {});
  }, [canFetchPendingApprovals]);

  const foremanAreaOptionsForDropdown = useMemo(
    () =>
      getForemanAreaOptions(
        currentUser?.foremanAreas,
        pendingServiceAreaId != null ? [pendingServiceAreaId] : []
      ),
    [currentUser?.foremanAreas, pendingServiceAreaId]
  );

  useEffect(() => {
    if (pendingForemanAreaId == null) return;
    const validIds = new Set(foremanAreaOptionsForDropdown.map((o) => o.id));
    if (!validIds.has(pendingForemanAreaId)) {
      setPendingForemanAreaId(null);
    }
  }, [foremanAreaOptionsForDropdown, pendingForemanAreaId]);

  const kpiFilter =
    foremanAreaNames.length > 0
      ? { foremanAreaNames }
      : undefined;

  // Submissions-style: send only foreman when foreman selected, else only service area (so backend returns correct count).
  // Use form filter options name (same as Submissions page) so backend filters by exact DB name.
  const serviceAreaNameForPending =
    selectedServiceAreaIds.length > 0 && selectedForemanAreaIds.length === 0
      ? (formFilterOptions?.serviceAreas?.find((sa) => Number(sa.id) === Number(selectedServiceAreaIds[0]))?.name ??
         serviceAreaNames[0])
      : undefined;
  const pendingFilter =
    selectedForemanAreaIds.length > 0
      ? { foremanArea: foremanAreaIdForForms }
      : selectedServiceAreaIds.length > 0
        ? { serviceArea: serviceAreaNameForPending ?? undefined }
        : undefined;

  const { counts, loading: countsLoading, error, refetch } = useKPITaskCounts(
    statuses,
    kpiFilter
  );

  const {
    pendingCount,
    pendingOver48hCount,
    loading: pendingLoading,
    error: pendingError,
    refetch: refetchPending,
  } = usePendingSubmissionsCount(canFetchPendingApprovals, pendingFilter);

  // Stacked bar chart (approval data) uses same applied filter for server-side scoping
  const approvalFilter = useMemo(
    () =>
      selectedForemanAreaIds.length > 0
        ? { foremanAreaId: selectedForemanAreaIds[0] }
        : selectedServiceAreaIds.length > 0
          ? { serviceAreaId: selectedServiceAreaIds[0] }
          : undefined,
    [selectedForemanAreaIds, selectedServiceAreaIds]
  );

  const {
    data: approvalData,
    loading: approvalLoading,
    error: approvalError,
    refresh: refetchApproval,
    lastUpdated: approvalLastUpdated,
  } = useApprovalData(approvalFilter);

  // Labour widget: same audience as the pending-approvals chart until it has its own permission
  const canViewLabourWidget = hasPermission('widget:pending_approvals_bar');
  const labourFilter = useMemo(
    () => ({
      serviceAreaId: selectedServiceAreaIds.length > 0 ? selectedServiceAreaIds[0] : undefined,
      foremanAreaIds: selectedForemanAreaIds,
    }),
    [selectedServiceAreaIds, selectedForemanAreaIds]
  );
  const {
    data: labourData,
    loading: labourLoading,
    error: labourError,
    refresh: refetchLabour,
    lastUpdated: labourLastUpdated,
  } = useLabourDaily(canViewLabourWidget, labourFilter);

  const newWorkIdFilter = useMemo(
    () =>
      selectedForemanAreaIds.length > 0
        ? { foremanAreaId: selectedForemanAreaIds[0] }
        : selectedServiceAreaIds.length > 0
          ? { serviceAreaId: selectedServiceAreaIds[0] }
          : undefined,
    [selectedForemanAreaIds, selectedServiceAreaIds]
  );

  const {
    data: newWorkIdData,
    loading: newWorkIdLoading,
    error: newWorkIdError,
    refetch: refetchNewWorkIds,
    lastUpdated: newWorkIdLastUpdated,
  } = useNewWorkIdCounts(newWorkIdFilter);

  const {
    data: taskAssignmentSummary,
    loading: taskAssignmentLoading,
    error: taskAssignmentError,
    refetch: refetchTaskAssignment,
    lastUpdated: taskAssignmentLastUpdated,
  } = useWorkIdTaskAssignmentSummary(newWorkIdFilter);

  const {
    count: urgentWorkIdCount,
    loading: urgentWorkIdLoading,
    error: urgentWorkIdError,
    refetch: refetchUrgent,
  } = useUrgentWorkIdCount(newWorkIdFilter);

  const completedCountsFilter = useMemo(
    () =>
      selectedForemanAreaIds.length > 0
        ? { foremanAreaNames }
        : selectedServiceAreaIds.length > 0 && serviceAreaNames.length > 0
          ? { serviceAreaName: serviceAreaNames[0] }
          : undefined,
    [selectedForemanAreaIds.length, selectedServiceAreaIds.length, foremanAreaNames, serviceAreaNames]
  );

  const {
    thisWeek: completedThisWeek,
    lastWeek: completedLastWeek,
    weekBoundaries,
    loading: completedLoading,
    error: completedError,
    refetch: refetchCompleted,
  } = useQuantifiedCompletedCounts(completedCountsFilter);

  const weekRange = useMemo(() => {
    if (weekBoundaries?.thisWeekStart && weekBoundaries?.thisWeekEnd) {
      return { start: weekBoundaries.thisWeekStart, end: weekBoundaries.thisWeekEnd };
    }
    return getCurrentWeekRangeLocal();
  }, [weekBoundaries?.thisWeekStart, weekBoundaries?.thisWeekEnd]);

  const yesterdayDate = useMemo(() => {
    const [y, m, d] = todayPacific().split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d - 1));
    return date.toISOString().slice(0, 10);
  }, []);

  const {
    overdueCount,
    loading: overdueLoading,
    error: overdueError,
    refetch: refetchOverdue,
  } = useOverdueTaskCount(
    OVERDUE_TASK_WIDGET_STATUSES.split(','),
    yesterdayDate,
    completedCountsFilter
  );

  const {
    count: plannedStartThisWeekCount,
    loading: plannedStartThisWeekLoading,
    error: plannedStartThisWeekError,
    refetch: refetchPlannedStartThisWeek,
  } = usePlannedStartThisWeekCount(
    PLANNED_START_WIDGET_STATUSES.split(','),
    weekRange.start,
    weekRange.end,
    completedCountsFilter
  );

  const redirectQueryParams = (widget: (typeof KPI_WIDGETS)[0]) => {
    const params: Record<string, string | number | boolean> = {
      status: widget.redirectStatus,
    };
    if (foremanAreaNames.length > 0) {
      params.foremanArea = foremanAreaNames.join(',');
    }
    return params;
  };

  const getOverdueWidgetRedirectParams = (): Record<string, string | number | boolean> => {
    const params: Record<string, string | number | boolean> = {
      status: OVERDUE_TASK_WIDGET_STATUSES,
      plannedCompletionDateTo: yesterdayDate,
    };
    if (foremanAreaNames.length > 0) {
      params.foremanArea = foremanAreaNames.join(',');
    } else if (serviceAreaNames.length > 0) {
      params.serviceArea = serviceAreaNames[0];
    }
    return params;
  };

  const getPlannedStartThisWeekRedirectParams = (): Record<string, string | number | boolean> => {
    const params: Record<string, string | number | boolean> = {
      status: PLANNED_START_WIDGET_STATUSES,
      plannedStartDateFrom: weekRange.start,
      plannedStartDateTo: weekRange.end,
    };
    if (foremanAreaNames.length > 0) {
      params.foremanArea = foremanAreaNames.join(',');
    } else if (serviceAreaNames.length > 0) {
      params.serviceArea = serviceAreaNames[0];
    }
    return params;
  };

  const submissionsRedirectParams: Record<string, string> = {
    status: 'pending',
  };
  if (selectedForemanAreaIds.length > 0 && foremanAreaIdForForms !== undefined) {
    submissionsRedirectParams.foremanArea = foremanAreaIdForForms;
  } else if (selectedServiceAreaIds.length > 0) {
    submissionsRedirectParams.serviceAreaId = String(selectedServiceAreaIds[0]);
  }

  const newWorkIdsRedirectParams: Record<string, string> = { status: 'New,Approved' };
  if (selectedForemanAreaIds.length > 0 && foremanAreaIdForForms !== undefined) {
    newWorkIdsRedirectParams.foremanAreaId = String(foremanAreaIdForForms);
  } else if (selectedServiceAreaIds.length > 0) {
    newWorkIdsRedirectParams.serviceAreaId = String(selectedServiceAreaIds[0]);
  }

  const pendingActionTotal = useMemo(
    () =>
      newWorkIdData.reduce(
        (sum, r) => sum + (r.newWorkIdCount ?? 0) + (r.approvedWorkIdCount ?? 0),
        0
      ),
    [newWorkIdData]
  );

  const urgentWorkIdsRedirectParams: Record<string, string> = {
    status: 'New,Approved',
    priority: 'Medium,High',
  };
  if (selectedForemanAreaIds.length > 0 && foremanAreaIdForForms !== undefined) {
    urgentWorkIdsRedirectParams.foremanAreaId = String(foremanAreaIdForForms);
  } else if (selectedServiceAreaIds.length > 0) {
    urgentWorkIdsRedirectParams.serviceAreaId = String(selectedServiceAreaIds[0]);
  }

  const handleApplyFilter = () => {
    setAreaFilters(
      pendingServiceAreaId != null ? [pendingServiceAreaId] : [],
      pendingForemanAreaId != null ? [pendingForemanAreaId] : []
    );
  };

  const handleClearAllFilters = () => {
    setAreaFilters([], []);
    setPendingServiceAreaId(null);
    setPendingForemanAreaId(null);
  };

  const hasAppliedFilters =
    selectedServiceAreaIds.length > 0 || selectedForemanAreaIds.length > 0;

  const pcrHomeServiceAreaId = useMemo(() => {
    if (selectedForemanAreaIds.length > 0) return undefined;
    const id = selectedServiceAreaIds[0];
    if (id === undefined) return undefined;
    return PCR_HOME_METRIC_SA_IDS.has(id) ? id : undefined;
  }, [selectedForemanAreaIds.length, selectedServiceAreaIds]);

  const canViewPcrHome = hasPermission('submission:view');
  // HE-M18 / V2-078. Gating the hook rather than only the tiles means a user without the
  // permission never issues the request.
  const canViewInsuranceExpiries = hasPermission('widget:insurance_expiries');
  const {
    counts: complianceCounts,
    loading: complianceLoading,
    error: complianceError,
    refetch: refetchCompliance,
  } = useVendorComplianceCounts(canViewInsuranceExpiries);
  // EQ-30: View Repairs. Gated at the hook so a user without it never sends the request.
  const canViewCriticalDefects = hasPermission('equipment:repairs_view');
  const {
    summary: criticalDefects,
    loading: criticalDefectsLoading,
    error: criticalDefectsError,
    refetch: refetchCriticalDefects,
  } = useCriticalDefects(canViewCriticalDefects);
  // V3-060: recent defects and overdue PMs / greasing per service area, with View Repairs.
  const {
    summary: equipmentHome,
    loading: equipmentHomeLoading,
    error: equipmentHomeError,
    refetch: refetchEquipmentHome,
  } = useEquipmentHome(canViewCriticalDefects);
  // V3-059: its own widget permission, gated at the hook.
  const canViewPendingTransfers = hasPermission(PENDING_TRANSFERS_WIDGET_PERMISSION);
  const {
    summary: pendingTransfers,
    loading: pendingTransfersLoading,
    error: pendingTransfersError,
    refetch: refetchPendingTransfers,
  } = usePendingTransfers(canViewPendingTransfers);
  const [pcrPieMonth, setPcrPieMonth] = useState(currentPcrChartMonth);
  const {
    metrics: pcrMetrics,
    loading: pcrMetricsLoading,
    error: pcrMetricsError,
    refetch: refetchPcrMetrics,
  } = usePcrHomeMetrics(canViewPcrHome, {
    serviceAreaId: pcrHomeServiceAreaId,
    foremanAreaId:
      selectedForemanAreaIds.length > 0 ? selectedForemanAreaIds[0] : undefined,
    chartMonth: pcrPieMonth,
  });

  const pcrMetricsErrorObj = pcrMetricsError ? new Error(pcrMetricsError) : undefined;

  const pcrBaseQuery = useMemo(() => {
    const q: Record<string, string> = {};
    if (pcrHomeServiceAreaId != null) q.serviceAreaId = String(pcrHomeServiceAreaId);
    if (selectedForemanAreaIds.length > 0) {
      q.foremanAreaId = String(selectedForemanAreaIds[0]);
    }
    return q;
  }, [pcrHomeServiceAreaId, selectedForemanAreaIds]);

  const pcrPieUsesResponseTimes =
    pcrHomeServiceAreaId != null || selectedForemanAreaIds.length > 0;

  const canViewPcrHomePie = hasPermission('widget:pcr_home_pie');

  const pcrPieTitle = useMemo(() => {
    if (pcrHomeServiceAreaId != null) {
      const name = getPcrWrapServiceAreaName(pcrHomeServiceAreaId) ?? 'Selected area';
      return `PCR Response Times – ${name}`;
    }
    if (selectedForemanAreaIds.length > 0 && foremanAreaNames.length > 0) {
      return `PCR Response Times – ${foremanAreaNames[0]}`;
    }
    return 'PCR Summaries by Service Area';
  }, [pcrHomeServiceAreaId, selectedForemanAreaIds.length, foremanAreaNames]);

  // The pie counts PCRs submitted in the chosen month; the New / Internal Review tiles beside it
  // count every open PCR. "No PCR data for the current filters" next to New = 237 read as a
  // contradiction, so the empty state names the month it is about.
  const pcrPieMonthLabel = formatPcrChartMonthLabel(pcrPieMonth);
  const pcrPieEmptyMessage = `No PCRs submitted in ${pcrPieMonthLabel}. The tiles count all open PCRs; pick another month to see its breakdown.`;

  const pcrPieRedirectQuery = useMemo(() => {
    const { from, to } = pcrChartMonthDateBounds(pcrPieMonth);
    return { ...pcrBaseQuery, from, to };
  }, [pcrBaseQuery, pcrPieMonth]);

  const pcrPieChartData = useMemo((): PcrPieChartData | undefined => {
    if (!pcrMetrics) return undefined;
    if (pcrPieUsesResponseTimes && pcrMetrics.byResponseTime) {
      const b = pcrMetrics.byResponseTime;
      const defs = [
        { label: '< 24hrs', count: b.lt24 },
        { label: '24–48hrs', count: b.between24_48 },
        { label: '> 48hrs', count: b.gt48 },
        { label: 'No response', count: b.noResponse },
      ];
      return {
        slices: defs.map((d, i) => ({
          ...d,
          color: COLOR_PALETTE[i % COLOR_PALETTE.length],
        })),
      };
    }
    const areas = pcrMetrics.byServiceArea;
    if (!areas?.length) return { slices: [] };
    // One colour per status so every service area's stacked bar reads the same way
    const statusColor: Record<(typeof PCR_STATUS_OPTIONS)[number]['id'], string> = {
      new: COLOR_PALETTE[0],
      internal_review: COLOR_PALETTE[1 % COLOR_PALETTE.length],
      finalized: COLOR_PALETTE[2 % COLOR_PALETTE.length],
    };
    return {
      slices: areas.map((a) => ({
        label: getPcrWrapServiceAreaName(a.serviceAreaId) ?? `Service Area ${a.serviceAreaId}`,
        count: a.count,
        color: statusColor.new,
        subSlices: PCR_STATUS_OPTIONS.map(({ id, name }) => ({
          label: name,
          count: a.byStatus[id],
          color: statusColor[id],
        })),
      })),
    };
  }, [pcrMetrics, pcrPieUsesResponseTimes]);

  if (authLoading) {
    return (
      <div className="tw-w-full tw-p-4">
        <h1 className="tw-mb-4">Home</h1>
        <div className="tw-text-content-muted">Loading...</div>
      </div>
    );
  }

  const showServiceAreaDropdown = serviceAreaOptions.length > 1;
  const showForemanAreaDropdown =
    (currentUser?.foremanAreas?.length ?? 0) > 1;
  const showAreaFilter =
    currentUser?.foremanAreas &&
    currentUser.foremanAreas.length > 0 &&
    (showServiceAreaDropdown || showForemanAreaDropdown);

  return (
    <div className="home-page-viewport tw-w-full tw-p-4" style={{ minWidth: 0 }}>
      <div className="tw-flex tw-flex-col xl:tw-flex-row xl:tw-flex-wrap tw-items-start xl:tw-items-center tw-justify-between tw-gap-3 tw-mb-2 home-page-header">
        <div className="home-page-welcome tw-min-w-0">
          <h2 className="tw-mb-2">
            Welcome to WRAP, {currentUser ? currentUser.name : ''}.</h2>
          <p className="tw-text-content-muted tw-mb-0 tw-max-w-2xl home-page-tagline">Your Essentials, Simplified. We've gathered the most important info right here to get you up to speed in seconds.</p>
        </div>
        {showAreaFilter && isTabletWidth && (
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            aria-controls="home-page-filters"
            className="tw-self-start"
          >
            <i className="bi bi-funnel tw-mr-1" aria-hidden="true" />
            Filters{hasAppliedFilters ? ' (Active)' : ''}
            <i className={`bi ${filtersOpen ? 'bi-chevron-up' : 'bi-chevron-down'} tw-ml-1`} aria-hidden="true" />
          </Button>
        )}
        {showAreaFilter && (!isTabletWidth || filtersOpen) && (
          <div id="home-page-filters" className="tw-flex tw-w-full tw-flex-wrap tw-items-end tw-gap-3 xl:tw-w-auto xl:tw-ml-auto home-page-filters home-page-filters-container" style={{ minWidth: 0 }}>
            {showServiceAreaDropdown && (
              <div className="home-page-filter-block">
                <StandoutDropdown
                  type="searchable"
                  options={serviceAreaOptions}
                  value={pendingServiceAreaId != null ? String(pendingServiceAreaId) : ''}
                  onChange={(v) => setPendingServiceAreaId(v === '' ? null : parseInt(v, 10) || null)}
                  getValue={(opt) => opt.id}
                  formatOption={(opt) => opt.name}
                  placeholder="All Service Areas"
                  label="Service Area"
                  noOptionsMessage="No service areas"
                />
              </div>
            )}
            {showForemanAreaDropdown && (
              <div className="home-page-filter-block">
                <StandoutDropdown
                  type="searchable"
                  options={foremanAreaOptionsForDropdown}
                  value={pendingForemanAreaId != null ? String(pendingForemanAreaId) : ''}
                  onChange={(v) => setPendingForemanAreaId(v === '' ? null : parseInt(v, 10) || null)}
                  getValue={(opt) => opt.id}
                  formatOption={(opt) => opt.name}
                  placeholder="All Foreman Areas"
                  label="Foreman Area"
                  noOptionsMessage="No foreman areas"
                />
              </div>
            )}
            <Button
              type="button"
              variant="primary"
              onClick={handleApplyFilter}
            >
              Apply
            </Button>
            {hasAppliedFilters && (
              <Button
                type="button"
                variant="outline-secondary"
                onClick={handleClearAllFilters}
              >
                Clear All
              </Button>
            )}
          </div>
        )}
      </div>
      {(canViewWidgets || canViewPcrHome || canViewCriticalDefects) && (
      <div className="home-page-content tw-flex tw-flex-col tw-gap-4">
        {canViewWidgets && (
        // items-stretch, not items-start: each column already wraps its section in
        // `home-group-card tw-h-full`, but h-full resolves against the column, and with
        // items-start a column is only as tall as its own content — so the three section cards
        // ended up different heights with ragged bottoms ("sits slightly higher than other
        // widgets"). Stretching the columns is what makes the existing h-full work.
        <Row className="tw-items-stretch home-page-three-columns">
          {/* Column 1: Submissions. Below xl (tablets) the three sections stack full width in
              this order instead of a cramped 2 + 1 grid (W-iP9). */}
          <Col xs={12} xl={4}>
            <div className="home-group-card tw-h-full">
              <div className="home-group-card-header tw-py-1 tw-px-2">
                <h6 className="tw-mb-0">Submissions</h6>
              </div>
              <div className="home-group-card-body tw-pt-2 tw-pb-2 submissions-card-body">
                <div className="tw-flex tw-flex-col tw-gap-3 tw-py-1">
                  <div>
                    <KPIHomeWidget
                      id="submissions-link"
                      title="Daily Log Submissions"
                      size="small"
                      requiredPermission="widget:pending_approvals_kpi"
                      data={{
                        value: pendingCount,
                        description: 'Submissions pending approvals',
                        // W-02: the 48-hour count belongs on the approvals widget, not Overdue Tasks
                        secondary: { value: pendingOver48hCount, label: 'pending over 48 hours' },
                      }}
                      redirect={{
                        path: '/submissions',
                        queryParams: submissionsRedirectParams,
                      }}
                      metadata={{
                        loading: pendingLoading,
                        error: pendingError ?? undefined,
                        isEmpty:
                          !pendingLoading &&
                          !pendingError &&
                          pendingCount === 0,
                      }}
                      onRefresh={refetchPending}
                    />
                  </div>
                  <div className="tw-grow">
                    <ServiceAreaBarGraphWidget
                      id="pending-approvals-by-area"
                      title="Pending Approvals by Foreman Area"
                      size="medium"
                      requiredPermission="widget:pending_approvals_bar"
                      data={approvalData}
                      config={SUBMISSIONS_BAR_CONFIG}
                      metadata={{
                        loading: approvalLoading,
                        error: approvalError,
                        lastUpdated: approvalLastUpdated,
                      }}
                      redirect={{
                        path: '/submissions',
                        queryParams: submissionsRedirectParams,
                      }}
                      onRefresh={refetchApproval}
                      legend={
                        <BarSegmentLegend
                          aria-label="Pending approvals key"
                          items={[
                            { label: 'Pending approval' },
                            { label: 'Revisions requested', darken: true },
                          ]}
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </Col>
          {/* Column 2: Quantified Task Summary */}
          <Col xs={12} xl={4}>
            <div className="home-group-card tw-h-full">
              <div className="home-group-card-header tw-py-1 tw-px-2">
                <h6 className="tw-mb-0">Quantified Task Summary</h6>
              </div>
              <div className="home-group-card-body tw-pt-2 tw-pb-2">
                <div className="kpi-widgets-grid home-quantified-task-grid">
                  {KPI_WIDGETS.map((widget) => {
                    const perm = WIDGET_ID_TO_PERMISSION[widget.id] ?? 'widget:active_tasks_kpi';
                    if (!hasPermission(perm)) return null;
                    return (
                      <div key={widget.id} className="tw-h-full">
                        <KPIHomeWidget
                          id={widget.id}
                          title={widget.title}
                          size="small"
                          requiredPermission={perm}
                          data={{
                            value: counts[widget.status] ?? 0,
                            description: widget.description,
                          }}
                          redirect={{
                            path: widget.redirectPath,
                            queryParams: redirectQueryParams(widget),
                          }}
                          metadata={{
                            loading: countsLoading,
                            error: error ?? undefined,
                            isEmpty:
                              !countsLoading &&
                              !error &&
                              (counts[widget.status] ?? 0) === 0,
                          }}
                          onRefresh={refetch}
                        />
                      </div>
                    );
                  })}
                  <div key="overdue-tasks" className="tw-h-full">
                    <KPIHomeWidget
                      id="overdue-tasks"
                      title="Overdue Tasks"
                      size="small"
                      requiredPermission="widget:overdue_tasks_kpi"
                      data={{
                        value: overdueCount,
                        description: 'Planned end date has passed',
                      }}
                      redirect={{
                        path: '/tasks/quantified',
                        queryParams: getOverdueWidgetRedirectParams(),
                      }}
                      metadata={{
                        loading: overdueLoading,
                        error: overdueError ?? undefined,
                        isEmpty:
                          !overdueLoading &&
                          !overdueError &&
                          overdueCount === 0,
                      }}
                      onRefresh={refetchOverdue}
                    />
                  </div>
                  <div key="planned-start-this-week" className="tw-h-full">
                    <KPIHomeWidget
                      id="planned-start-this-week"
                      title="Tasks Planned Start This Week"
                      size="small"
                      requiredPermission="widget:planned_start_this_week_kpi"
                      data={{
                        value: plannedStartThisWeekCount,
                        description: `This week (${formatWeekRangeLabel(weekRange.start, weekRange.end)})`,
                      }}
                      redirect={{
                        path: '/tasks/quantified',
                        queryParams: getPlannedStartThisWeekRedirectParams(),
                      }}
                      metadata={{
                        loading: plannedStartThisWeekLoading,
                        error: plannedStartThisWeekError ?? undefined,
                        isEmpty:
                          !plannedStartThisWeekLoading &&
                          !plannedStartThisWeekError &&
                          plannedStartThisWeekCount === 0,
                      }}
                      onRefresh={refetchPlannedStartThisWeek}
                    />
                  </div>
                  {hasPermission('widget:completed_last_week_kpi') && (
                    <div key="completed-last-week" className="tw-h-full">
                      <KPIHomeWidget
                        id="completed-last-week"
                        title="Tasks Completed Last Week"
                        size="small"
                        requiredPermission="widget:completed_last_week_kpi"
                        data={{
                          value: completedLastWeek,
                          description: weekBoundaries
                            ? `Last week (${formatWeekRangeLabel(weekBoundaries.lastWeekStart, weekBoundaries.lastWeekEnd)})`
                            : 'Last week',
                        }}
                        metadata={{
                          loading: completedLoading,
                          error: completedError ?? undefined,
                          isEmpty:
                            !completedLoading &&
                            !completedError &&
                            completedLastWeek === 0,
                        }}
                        onRefresh={refetchCompleted}
                      />
                    </div>
                  )}
                  <div key="completed-this-week" className="tw-h-full">
                    <KPIHomeWidget
                      id="completed-this-week"
                      title="Tasks Completed This Week"
                      size="small"
                      requiredPermission="widget:completed_this_week_kpi"
                      data={{
                        value: completedThisWeek,
                        description: weekBoundaries
                          ? `This week (${formatWeekRangeLabel(weekBoundaries.thisWeekStart, weekBoundaries.thisWeekEnd)})`
                          : 'This week',
                      }}
                      metadata={{
                        loading: completedLoading,
                        error: completedError ?? undefined,
                        isEmpty:
                          !completedLoading &&
                          !completedError &&
                          completedThisWeek === 0,
                      }}
                      onRefresh={refetchCompleted}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Col>
          {/* Column 3: Work IDs */}
          <Col xs={12} xl={4}>
            <div className="home-group-card tw-h-full">
              <div className="home-group-card-header tw-py-1 tw-px-2">
                <h6 className="tw-mb-0">Work IDs</h6>
              </div>
              <div className="home-group-card-body tw-pt-2 tw-pb-2">
                <div className="kpi-widgets-grid home-work-ids-kpi-grid">
                  {hasPermission('widget:work_id_pending_action_kpi') && (
                    <div className="tw-h-full">
                      <KPIHomeWidget
                        id="work-id-pending-action"
                        title="Work ID Pending Action"
                        size="small"
                        requiredPermission="widget:work_id_pending_action_kpi"
                        data={{
                          value: pendingActionTotal,
                          description: 'New + Approved Work IDs',
                        }}
                        redirect={{
                          path: '/work-ids',
                          queryParams: newWorkIdsRedirectParams,
                        }}
                        metadata={{
                          loading: newWorkIdLoading,
                          error: newWorkIdError ?? undefined,
                          isEmpty:
                            !newWorkIdLoading &&
                            !newWorkIdError &&
                            pendingActionTotal === 0,
                        }}
                        onRefresh={refetchNewWorkIds}
                      />
                    </div>
                  )}
                  {hasPermission('widget:urgent_work_ids_kpi') && (
                    <div className="tw-h-full">
                      <KPIHomeWidget
                        id="urgent-work-ids"
                        title="Urgent Work IDs"
                        size="small"
                        requiredPermission="widget:urgent_work_ids_kpi"
                        data={{
                          value: urgentWorkIdCount,
                          description: 'New/Approved with Medium or High priority',
                        }}
                        redirect={{
                          path: '/work-ids',
                          queryParams: urgentWorkIdsRedirectParams,
                        }}
                        metadata={{
                          loading: urgentWorkIdLoading,
                          error: urgentWorkIdError ?? undefined,
                          isEmpty:
                            !urgentWorkIdLoading &&
                            !urgentWorkIdError &&
                            urgentWorkIdCount === 0,
                        }}
                        onRefresh={refetchUrgent}
                      />
                    </div>
                  )}
                </div>
                {hasPermission('widget:new_work_ids_bar') && (
                  <div className="tw-mt-2 tw-h-full">
                    <ServiceAreaBarGraphWidget
                      id="new-work-ids-by-area"
                      title="Work IDs Pending Action by Foreman Area"
                      size="medium"
                      requiredPermission="widget:new_work_ids_bar"
                      data={newWorkIdData}
                      config={NEW_WORK_IDS_BAR_CONFIG}
                      metadata={{
                        loading: newWorkIdLoading,
                        error: newWorkIdError,
                        lastUpdated: newWorkIdLastUpdated,
                      }}
                      redirect={{
                        path: '/work-ids',
                        queryParams: newWorkIdsRedirectParams,
                      }}
                      onRefresh={refetchNewWorkIds}
                    />
                  </div>
                )}
                {SHOW_WORK_ID_TASK_ASSIGNMENT_WIDGET &&
                  hasPermission('widget:work_id_task_assignment') && (
                  <div className="tw-mt-2 tw-h-full">
                    <WorkIdTaskAssignmentWidget
                      id="work-id-task-assignment-summary"
                      title="Assigned Work IDs – planned, active & review tasks"
                      size="medium"
                      requiredPermission="widget:work_id_task_assignment"
                      data={taskAssignmentSummary ?? undefined}
                      metadata={{
                        loading: taskAssignmentLoading,
                        error: taskAssignmentError,
                        lastUpdated: taskAssignmentLastUpdated,
                      }}
                      onRefresh={refetchTaskAssignment}
                    />
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
        )}
        {canViewLabourWidget && (
        <Row className="home-page-pcr-bottom">
          <Col xs={12} className="tw-min-w-0">
            <div className="home-group-card tw-h-full">
              <div className="home-group-card-header tw-py-1 tw-px-2">
                <h6 className="tw-mb-0">Labour</h6>
              </div>
              <div className="home-group-card-body tw-pt-2 tw-pb-2">
                <LabourStackedBarWidget
                  id="labour-daily"
                  title="Labour by task type, last 7 days"
                  requiredPermission="widget:pending_approvals_bar"
                  data={labourData}
                  metadata={{
                    loading: labourLoading,
                    error: labourError,
                    lastUpdated: labourLastUpdated,
                  }}
                  onRefresh={refetchLabour}
                />
              </div>
            </div>
          </Col>
        </Row>
        )}
        {canViewPcrHome && (
        <Row className="home-page-pcr-bottom">
          <Col xs={12} className="tw-min-w-0">
            <div className="home-group-card tw-h-full">
              <div className="home-group-card-header tw-py-1 tw-px-2">
                <h6 className="tw-mb-0">Public PCR (Website)</h6>
              </div>
              <div className="home-group-card-body tw-pt-2 tw-pb-2 pcr-group-card-body home-pcr-group-body">
                <div className="kpi-widgets-grid home-pcr-widgets-grid">
                    <div className="tw-h-full">
                      <KPIHomeWidget
                        id="pcr-new"
                        title="New"
                        size="small"
                        requiredPermission="submission:view"
                        data={{
                          value: pcrMetrics?.['new'] ?? 0,
                          description: 'Not yet in internal review',
                        }}
                        redirect={{
                          path: '/pcrs',
                          queryParams: { ...pcrBaseQuery, status: 'new' },
                        }}
                        metadata={{
                          loading: pcrMetricsLoading,
                          error: pcrMetricsErrorObj,
                          isEmpty:
                            !pcrMetricsLoading &&
                            !pcrMetricsError &&
                            (pcrMetrics?.['new'] ?? 0) === 0,
                        }}
                        onRefresh={refetchPcrMetrics}
                      />
                    </div>
                  <div className="tw-h-full">
                    <KPIHomeWidget
                      id="pcr-internal-review"
                      title="Internal Review"
                      size="small"
                      requiredPermission="submission:view"
                      data={{
                        value: pcrMetrics?.internal_review ?? 0,
                        description: 'In internal review',
                      }}
                      redirect={{
                        path: '/pcrs',
                        queryParams: { ...pcrBaseQuery, status: 'internal_review' },
                      }}
                      metadata={{
                        loading: pcrMetricsLoading,
                        error: pcrMetricsErrorObj,
                        isEmpty:
                          !pcrMetricsLoading &&
                          !pcrMetricsError &&
                          (pcrMetrics?.internal_review ?? 0) === 0,
                      }}
                      onRefresh={refetchPcrMetrics}
                    />
                  </div>
                  <div className="tw-h-full">
                    <KPIHomeWidget
                      id="pcr-not-actioned-24h"
                      title="Not Actioned (> 24hrs)"
                      size="small"
                      requiredPermission="submission:view"
                      data={{
                        value: pcrMetrics?.notActionedOver24h ?? 0,
                        description: 'No first response for over 24 hours',
                      }}
                      redirect={{
                        path: '/pcrs',
                        queryParams: { ...pcrBaseQuery, status: 'new' },
                      }}
                      metadata={{
                        loading: pcrMetricsLoading,
                        error: pcrMetricsErrorObj,
                        isEmpty:
                          !pcrMetricsLoading &&
                          !pcrMetricsError &&
                          (pcrMetrics?.notActionedOver24h ?? 0) === 0,
                      }}
                      onRefresh={refetchPcrMetrics}
                    />
                  </div>
                  {canViewPcrHomePie && (
                    <div className="tw-h-full home-pcr-pie-cell tw-min-w-0">
                      <PcrStackedBarWidget
                        id="pcr-home-pie"
                        title={`${pcrPieTitle} – ${pcrPieMonthLabel}`}
                        emptyMessage={pcrPieEmptyMessage}
                        size="small"
                        singleBarLabel="Responses"
                        data={pcrPieChartData}
                        redirect={{
                          path: '/pcrs',
                          queryParams: pcrPieRedirectQuery,
                        }}
                        metadata={{
                          loading: pcrMetricsLoading,
                          error: pcrMetricsErrorObj,
                          lastUpdated: null,
                        }}
                        onRefresh={refetchPcrMetrics}
                        requiredPermission="widget:pcr_home_pie"
                        headerExtra={
                          <Form.Control
                            id="pcr-pie-month"
                            type="month"
                            size="sm"
                            className="home-pcr-pie-month-input"
                            value={pcrPieMonth}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              if (v) setPcrPieMonth(v);
                            }}
                            title={formatPcrChartMonthLabel(pcrPieMonth)}
                            aria-label={`Chart month, ${formatPcrChartMonthLabel(pcrPieMonth)}`}
                          />
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        )}
        {canViewCriticalDefects && (
        <Row>
          <Col xs={12} className="tw-min-w-0">
            <div className="home-group-card tw-h-full">
              <div className="home-group-card-header tw-py-1 tw-px-2">
                <h6 className="tw-mb-0">Critical Defects</h6>
              </div>
              <div className="home-group-card-body tw-pt-2 tw-pb-2">
                <CriticalDefectsWidget
                  summary={criticalDefects}
                  loading={criticalDefectsLoading}
                  error={criticalDefectsError}
                  onRetry={refetchCriticalDefects}
                  requiredPermission="equipment:repairs_view"
                />
              </div>
            </div>
          </Col>
        </Row>
        )}
        {canViewCriticalDefects && (
        <Row>
          <Col xs={12} className="tw-min-w-0">
            <div className="home-group-card tw-h-full">
              <div className="home-group-card-header tw-py-1 tw-px-2">
                <h6 className="tw-mb-0">Equipment</h6>
              </div>
              <div className="home-group-card-body tw-pt-2 tw-pb-2">
                <EquipmentHomeWidget
                  summary={equipmentHome}
                  loading={equipmentHomeLoading}
                  error={equipmentHomeError}
                  onRetry={refetchEquipmentHome}
                  requiredPermission="equipment:repairs_view"
                />
              </div>
            </div>
          </Col>
        </Row>
        )}
        {canViewPendingTransfers && (
        <Row>
          <Col xs={12} className="tw-min-w-0">
            <div className="home-group-card tw-h-full">
              <div className="home-group-card-header tw-py-1 tw-px-2">
                <h6 className="tw-mb-0">Pending Transfers</h6>
              </div>
              <div className="home-group-card-body tw-pt-2 tw-pb-2">
                <PendingTransfersWidget
                  summary={pendingTransfers}
                  loading={pendingTransfersLoading}
                  error={pendingTransfersError}
                  onRetry={refetchPendingTransfers}
                  canOpenTransfers={hasPermission('vehicle:view')}
                />
              </div>
            </div>
          </Col>
        </Row>
        )}
        {canViewInsuranceExpiries && (
        <Row className="home-page-pcr-bottom">
          <Col xs={12} className="tw-min-w-0">
            <div className="home-group-card tw-h-full">
              <div className="home-group-card-header tw-py-1 tw-px-2">
                <h6 className="tw-mb-0">Vendor Compliance</h6>
              </div>
              <div className="home-group-card-body tw-pt-2 tw-pb-2">
                {/* V2-078: three count tiles in the KPI style – no list and no inner scroll box.
                    Each opens the Vendor Compliance report filtered to its status. */}
                <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-3" data-testid="vendor-compliance-tiles">
                  {(
                    [
                      { id: 'compliance-overdue', title: 'Overdue', status: 'OVERDUE', key: 'overdue', description: 'Compliance documents past their expiry date' },
                      { id: 'compliance-due-soon', title: 'Due in 30 Days', status: 'DUE_SOON', key: 'dueSoon', description: 'Compliance documents expiring within 30 days' },
                      { id: 'compliance-missing', title: 'Missing Documents', status: 'MISSING', key: 'missing', description: 'Required compliance documents not on file' },
                    ] as const
                  ).map((tile) => (
                    <KPIHomeWidget
                      key={tile.id}
                      id={tile.id}
                      title={tile.title}
                      size="small"
                      requiredPermission="widget:insurance_expiries"
                      data={{
                        value: complianceCounts?.[tile.key] ?? 0,
                        description: tile.description,
                      }}
                      redirect={{
                        path: '/reports',
                        queryParams: { report: VENDOR_COMPLIANCE_SECTION, status: tile.status },
                      }}
                      metadata={{
                        loading: complianceLoading,
                        error: complianceError ?? undefined,
                      }}
                      onRefresh={refetchCompliance}
                    />
                  ))}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        )}
      </div>
      )}
      {/* V3-006: Home is never blank – a role with no tiles is told so. */}
      {!(canViewWidgets || canViewPcrHome || canViewCriticalDefects) && (
        <p className="tw-mt-4 tw-text-content-muted" role="status">
          Nothing to show on Home for your role yet.
        </p>
      )}
    </div>
  );
};

export default HomePage;
